import React, { FC, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Button, Dialog } from "@radix-ui/themes";
import { api } from "@/modules/api";
import { TERMINAL_FONT_FAMILY } from "./shared/constants";

interface SessionAgentRun {
  id: string;
  associated_session_id: string;
  metadata: {
    associated_datadog_trace_id?: string;
  };
  internal_created_at: string;
  internal_updated_at: string;
}

interface AgentRunsModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
}

export const AgentRunsModal: FC<AgentRunsModalProps> = ({ isOpen, onClose, sessionId }) => {
  const [agentRuns, setAgentRuns] = useState<SessionAgentRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAgentRuns = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post("/internal/v1/session-agent-runs/search", {
        body: {
          filter_by_session_id: sessionId,
          limit: 50,
          offset: 0,
        },
      });

      if (response.data?.results) {
        setAgentRuns(response.data.results);
      }
    } catch (err) {
      setError(`Failed to fetch agent runs: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchAgentRuns();
    }
  }, [isOpen, sessionId]);

  const handleViewTrace = (traceId: string) => {
    // Convert trace ID to hex format for Datadog URL
    const datadogUrl = `https://app.datadoghq.com/apm/trace/${traceId}`;
    window.open(datadogUrl, "_blank");
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Content style={{ maxWidth: "95vw", width: "1000px", maxHeight: "80vh" }}>
        <DialogTitle>Agent Runs for Session</DialogTitle>
        <DialogDescription size="2" mb="4">
          All session agent runs for session ID: {sessionId}
        </DialogDescription>

        {loading && (
          <LoadingContainer>
            <div>Loading agent runs...</div>
          </LoadingContainer>
        )}

        {error && (
          <ErrorContainer>
            <div>{error}</div>
            <Button onClick={fetchAgentRuns} size="1" style={{ marginTop: "8px" }}>
              Retry
            </Button>
          </ErrorContainer>
        )}

        {!loading && !error && (
          <TableContainer>
            {agentRuns.length === 0 ? (
              <EmptyState>No agent runs found for this session.</EmptyState>
            ) : (
              <Table>
                <TableHeader>
                  <HeaderRow>
                    <HeaderCell>Created At</HeaderCell>
                    <HeaderCell>ID</HeaderCell>
                    <HeaderCell>Actions</HeaderCell>
                  </HeaderRow>
                </TableHeader>
                <TableBody>
                  {agentRuns.map(run => (
                    <TableRow key={run.id}>
                      <TableCell>{formatDateTime(run.internal_created_at)}</TableCell>
                      <TableCell>
                        <MonospaceText>{run.id}</MonospaceText>
                      </TableCell>
                      <TableCell>
                        {run.metadata?.associated_datadog_trace_id && (
                          <TraceButton
                            size="1"
                            variant="outline"
                            onClick={() =>
                              handleViewTrace(run.metadata.associated_datadog_trace_id!)
                            }
                          >
                            View Trace
                          </TraceButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        )}

        <ActionButtons>
          <Button onClick={onClose}>Close</Button>
        </ActionButtons>
      </Dialog.Content>
    </Dialog.Root>
  );
};

const DialogTitle = styled(Dialog.Title)`
  font-family: ${TERMINAL_FONT_FAMILY};
  font-size: 13px;
  font-weight: 600;
`;

const DialogDescription = styled(Dialog.Description)`
  font-family: ${TERMINAL_FONT_FAMILY};
  font-size: 13px;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  font-family: ${TERMINAL_FONT_FAMILY};
  font-size: 13px;
  color: var(--gray-10);
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  font-family: ${TERMINAL_FONT_FAMILY};
  font-size: 13px;
  color: var(--red-11);
`;

const TableContainer = styled.div`
  max-height: 500px;
  overflow: auto;
  border: 1px solid var(--gray-6);
  margin-bottom: 16px;

  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${TERMINAL_FONT_FAMILY};
  font-size: 11px;
  min-width: 400px;

  @media (max-width: 768px) {
    min-width: 300px;
    font-size: 10px;
  }
`;

const TableHeader = styled.thead`
  position: sticky;
  top: 0;
  background-color: var(--gray-2);
`;

const HeaderRow = styled.tr`
  border-bottom: 2px solid var(--gray-6);
`;

const HeaderCell = styled.th`
  padding: 8px 6px;
  text-align: left;
  font-weight: 600;
  color: var(--gray-12);
  border-right: 1px solid var(--gray-6);
  white-space: nowrap;

  &:last-child {
    border-right: none;
  }

  @media (max-width: 768px) {
    padding: 6px 4px;
    font-size: 10px;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid var(--gray-4);

  &:hover {
    background-color: var(--gray-1);
  }
`;

const TableCell = styled.td`
  padding: 8px 6px;
  vertical-align: top;
  border-right: 1px solid var(--gray-4);
  max-width: 200px;
  overflow: hidden;

  &:last-child {
    border-right: none;
  }

  @media (max-width: 768px) {
    padding: 6px 4px;
    max-width: 150px;
  }
`;

const MonospaceText = styled.span`
  font-family: ${TERMINAL_FONT_FAMILY};
  font-size: 10px;
  word-break: break-all;

  @media (max-width: 768px) {
    font-size: 9px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--gray-10);
  font-family: ${TERMINAL_FONT_FAMILY};
  font-size: 13px;
  font-style: italic;
`;

const TraceButton = styled(Button)`
  font-size: 10px;
  font-family: ${TERMINAL_FONT_FAMILY};
  min-height: auto;
  height: auto;
  padding: 2px 6px;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;
